import React, { useState } from 'react';
import { 
  MessageCircle, 
  Mail, 
  Send, 
  Search, 
  Filter,
  Clock,
  CheckCircle,
  User,
  Plus,
  Paperclip,
  Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';

const OrganizationCommunication = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Mock conversations data
  const conversations = [
    {
      id: 1,
      trainer: {
        name: 'Dr. Sarah Johnson',
        avatar: null,
        specialization: 'Leadership Development',
        status: 'online'
      },
      lastMessage: "I've prepared the materials for next week's leadership workshop. Should I send them for review?",
      timestamp: '2024-09-28T10:30:00',
      unreadCount: 2,
      type: 'active',
      messages: [
        {
          id: 1,
          sender: 'trainer',
          content: "Good morning! I wanted to update you on the preparation progress for the leadership workshop.",
          timestamp: '2024-09-28T09:00:00',
          read: true
        },
        {
          id: 2,
          sender: 'organization',
          content: "Thank you for the update. How are things progressing?",
          timestamp: '2024-09-28T09:15:00',
          read: true
        },
        {
          id: 3,
          sender: 'trainer',
          content: "Everything is on track. I've completed the presentation materials and prepared the interactive exercises.",
          timestamp: '2024-09-28T10:00:00',
          read: true
        },
        {
          id: 4,
          sender: 'trainer',
          content: "I've prepared the materials for next week's leadership workshop. Should I send them for review?",
          timestamp: '2024-09-28T10:30:00',
          read: false
        }
      ]
    },
    {
      id: 2,
      trainer: {
        name: 'Michael Chen',
        avatar: null,
        specialization: 'Technical Training',
        status: 'offline'
      },
      lastMessage: "The virtual platform setup is complete. I'll send the access details tomorrow.",
      timestamp: '2024-09-27T16:45:00',
      unreadCount: 0,
      type: 'completed',
      messages: [
        {
          id: 1,
          sender: 'organization',
          content: "Hi Michael, can you provide an update on the virtual training setup?",
          timestamp: '2024-09-27T14:00:00',
          read: true
        },
        {
          id: 2,
          sender: 'trainer',
          content: "I'm configuring the virtual platform now. Should be ready by end of day.",
          timestamp: '2024-09-27T15:30:00',
          read: true
        },
        {
          id: 3,
          sender: 'trainer',
          content: "The virtual platform setup is complete. I'll send the access details tomorrow.",
          timestamp: '2024-09-27T16:45:00',
          read: true
        }
      ]
    },
    {
      id: 3,
      trainer: {
        name: 'Emily Rodriguez',
        avatar: null,
        specialization: 'Project Management',
        status: 'away'
      },
      lastMessage: "Could we reschedule our planning call to Friday afternoon?",
      timestamp: '2024-09-26T11:20:00',
      unreadCount: 1,
      type: 'pending',
      messages: [
        {
          id: 1,
          sender: 'trainer',
          content: "I need to discuss some changes to the project management curriculum.",
          timestamp: '2024-09-26T10:00:00',
          read: true
        },
        {
          id: 2,
          sender: 'organization',
          content: "Sure, let's schedule a call to discuss the details.",
          timestamp: '2024-09-26T10:30:00',
          read: true
        },
        {
          id: 3,
          sender: 'trainer',
          content: "Could we reschedule our planning call to Friday afternoon?",
          timestamp: '2024-09-26T11:20:00',
          read: false
        }
      ]
    },
    {
      id: 4,
      trainer: {
        name: 'James Wilson',
        avatar: null,
        specialization: 'Team Development',
        status: 'online'
      },
      lastMessage: "Thank you for the quick approval! I'll start the venue booking process.",
      timestamp: '2024-09-25T14:15:00',
      unreadCount: 0,
      type: 'active',
      messages: [
        {
          id: 1,
          sender: 'trainer',
          content: "I've found a great outdoor venue for the team building retreat. Can I proceed with booking?",
          timestamp: '2024-09-25T13:00:00',
          read: true
        },
        {
          id: 2,
          sender: 'organization',
          content: "That sounds perfect! Please go ahead and book it.",
          timestamp: '2024-09-25T13:30:00',
          read: true
        },
        {
          id: 3,
          sender: 'trainer',
          content: "Thank you for the quick approval! I'll start the venue booking process.",
          timestamp: '2024-09-25T14:15:00',
          read: true
        }
      ]
    }
  ];

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.trainer.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || conv.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      case 'offline': return 'bg-gray-400';
      default: return 'bg-gray-400';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  };

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation) {
      const newMsg = {
        id: selectedConversation.messages.length + 1,
        sender: 'organization',
        content: newMessage,
        timestamp: new Date().toISOString(),
        read: true
      };
      
      // In a real app, this would be handled by state management
      console.log('Sending message:', newMsg);
      setNewMessage('');
      alert('Message sent successfully!');
    }
  };

  const handleStartNewConversation = () => {
    alert('Opening trainer selection dialog...');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MessageCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Communication Center</h1>
            <p className="text-gray-600">Messages and communication with trainers</p>
          </div>
        </div>
        <Button
          onClick={handleStartNewConversation}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      {/* Communication Interface */}
      <div className="grid grid-cols-12 gap-6 h-[600px]">
        {/* Conversations List */}
        <div className="col-span-4">
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-3">
              <div className="space-y-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                {/* Filter */}
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Conversations</option>
                  <option value="active">Active Projects</option>
                  <option value="pending">Pending Response</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </CardHeader>
            <CardContent className="flex-1 p-0 overflow-y-auto">
              <div className="space-y-1">
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 border-b transition-colors ${
                      selectedConversation?.id === conversation.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="relative">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={conversation.trainer.avatar} alt={conversation.trainer.name} />
                          <AvatarFallback className="bg-blue-600 text-white text-sm">
                            {conversation.trainer.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(conversation.trainer.status)}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900 truncate">{conversation.trainer.name}</h4>
                          <div className="flex items-center space-x-1">
                            {conversation.unreadCount > 0 && (
                              <Badge variant="destructive" className="text-xs px-2">
                                {conversation.unreadCount}
                              </Badge>
                            )}
                            <span className="text-xs text-gray-500">{formatTime(conversation.timestamp)}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{conversation.trainer.specialization}</p>
                        <p className="text-sm text-gray-700 truncate">{conversation.lastMessage}</p>
                        <Badge className={`${getTypeColor(conversation.type)} text-xs mt-1`}>
                          {conversation.type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Message View */}
        <div className="col-span-8">
          <Card className="h-full flex flex-col">
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <CardHeader className="pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={selectedConversation.trainer.avatar} alt={selectedConversation.trainer.name} />
                          <AvatarFallback className="bg-blue-600 text-white">
                            {selectedConversation.trainer.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${getStatusColor(selectedConversation.trainer.status)}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedConversation.trainer.name}</h3>
                        <p className="text-sm text-gray-600">{selectedConversation.trainer.specialization}</p>
                        <p className="text-xs text-gray-500 capitalize">{selectedConversation.trainer.status}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button variant="outline" size="sm">
                        <Star className="w-4 h-4 mr-1" />
                        Favorite
                      </Button>
                      <Button variant="outline" size="sm">
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <CardContent className="flex-1 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    {selectedConversation.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender === 'organization' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.sender === 'organization'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}>
                          <p className="text-sm">{message.content}</p>
                          <div className="flex items-center justify-between mt-1">
                            <p className={`text-xs ${
                              message.sender === 'organization' ? 'text-blue-100' : 'text-gray-500'
                            }`}>
                              {formatTime(message.timestamp)}
                            </p>
                            {message.sender === 'organization' && (
                              <CheckCircle className={`w-3 h-3 ${message.read ? 'text-blue-200' : 'text-blue-300'}`} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm">
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                  <p className="text-gray-500 mb-4">Choose a conversation from the list to start messaging</p>
                  <Button onClick={handleStartNewConversation} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Start New Conversation
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Communication Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Communication Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {conversations.length}
              </div>
              <div className="text-sm text-blue-800">Total Conversations</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {conversations.filter(c => c.type === 'active').length}
              </div>
              <div className="text-sm text-green-800">Active Projects</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {conversations.reduce((sum, c) => sum + c.unreadCount, 0)}
              </div>
              <div className="text-sm text-yellow-800">Unread Messages</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {conversations.filter(c => c.trainer.status === 'online').length}
              </div>
              <div className="text-sm text-purple-800">Online Trainers</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationCommunication;